import { Plus } from 'lucide-react'
import { Alert, Badge, Button, Card, CardBody, CardHeader, SkeletonList, Switch } from '@/components/ui'
import { ConfirmDialog, QueryBoundary } from '@/components/domain'
import { useDisclosure } from '@/hooks'
import { useT } from '@/internationalization/i18n'
import { PERMISSIONS } from '@/features/authorization/permissions'
import { useSessionStore } from '@/stores'
import { useRecordingRules, useRecordingSettings, useToggleRule } from '../hooks'

/**
 * Whether the recorder also captures the SHARED SCREEN.
 *
 * TURNING THIS ON IS A WIDER ACT THAN IT LOOKS, which is why it confirms. The
 * meeting invite is this product's consent artefact (D-17) and it is an invite
 * to a MEETING -- nobody agreed to having the window they share recorded. A
 * shared screen routinely holds a customer's PII, somebody's inbox, or a
 * document belonging to a third party who is not in the room. And nothing
 * in-meeting discloses that the bot is recording anything (CR-1).
 *
 * Its own permission, not `rule:manage`: migration 028 exists so a workspace
 * can have an admin who administers settings and may never open a recording,
 * and that same admin must not be able to start capturing client screens.
 */
function ScreenCaptureCard() {
  const permissions = useSessionStore((s) => s.permissions)
  // Fails closed -- null until the session is established.
  const allowed = permissions?.includes(PERMISSIONS.meetingScreenCapture) ?? false
  const { query, save } = useRecordingSettings(allowed)
  const confirm = useDisclosure()

  if (!allowed) return null

  const enabled = query.data?.screenCaptureEnabled ?? false

  return (
    <>
      <Card>
        <CardHeader
          title="Screen capture"
          description="Record the shared screen alongside the audio."
        />
        <QueryBoundary
          isLoading={query.isPending}
          error={query.error}
          onRetry={query.refetch}
          skeleton={<SkeletonList rows={2} className="p-4" />}
        >
          <CardBody className="space-y-3">
            <Switch
              label="Capture the shared screen by default"
              showLabel
              checked={enabled}
              disabled={save.isPending}
              onChange={(next: boolean) => {
                if (next) confirm.open()
                else save.mutate({ screenCaptureEnabled: false })
              }}
              description={
                enabled
                  ? 'Every meeting in this workspace captures the shared screen unless it is turned off for that meeting.'
                  : 'Off. You can still turn it on for a single meeting from Up next.'
              }
            />
            <p className="text-sm text-fg-subtle">
              A shared window often holds more than slides — an inbox, a customer's
              records, a document belonging to somebody who is not in the call.
              Nothing in the meeting tells people this is happening.
            </p>
          </CardBody>
        </QueryBoundary>
      </Card>

      <ConfirmDialog
        isOpen={confirm.isOpen}
        onCancel={confirm.close}
        title="Capture the shared screen in every meeting?"
        description={
          'From now on the recorder stores images of whatever is shared on screen ' +
          'in every meeting it joins — not just slides. People are not told this ' +
          'is happening, and the invite they accepted did not ask them.'
        }
        confirmLabel="Turn on"
        isDestructive
        isLoading={save.isPending}
        onConfirm={() => {
          save.mutate({ screenCaptureEnabled: true })
          confirm.close()
        }}
      />
    </>
  )
}


/**
 * Which meetings get recorded.
 *
 * Rules are evaluated top to bottom and the first match wins, which is stated
 * on the page — a rule list whose order is invisible is a rule list nobody can
 * predict. The default is not to record: a meeting that slips through because
 * nobody wrote a rule is a recording nobody consented to.
 */
export function RecordingRulesSettings() {
  const t = useT()
  const { data, isPending, error, refetch } = useRecordingRules()
  const toggle = useToggleRule()

  return (
    <div className="space-y-4">
      <Alert tone="info" title={t('recording.rulesInOrderTitle')}>
        {t('recording.rulesInOrderBodyPrefix')}
        <strong>{t('recording.rulesInOrderNot')}</strong>
        {t('recording.rulesInOrderBodySuffix')}
      </Alert>

      <ScreenCaptureCard />

      <Card>
        <CardHeader
          title={t('recording.rulesTitle')}
          action={
            <Button size="sm" leftIcon={<Plus className="h-4 w-4" />}>
              {t('recording.addRule')}
            </Button>
          }
        />
        <QueryBoundary
          isLoading={isPending}
          error={error}
          onRetry={refetch}
          skeleton={<SkeletonList rows={4} className="p-4" />}
        >
          <ul className="divide-y divide-border">
            {data?.map((rule, index) => (
              <li key={rule.id} className="flex items-start gap-3 px-4 py-3">
                <span className="mt-0.5 w-5 shrink-0 text-sm tabular-nums text-fg-subtle">{index + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-base font-medium text-fg">{rule.name}</p>
                    <Badge tone={rule.action === 'record' ? 'success' : 'neutral'}>{rule.action}</Badge>
                    <Badge tone="neutral">{rule.scope}</Badge>
                  </div>
                  <p className="mt-0.5 text-sm text-fg-muted">{rule.condition}</p>
                </div>
                <Switch
                  label={t('recording.enableRule', { name: rule.name })}
                  checked={rule.enabled}
                  onChange={(enabled) => toggle.mutate({ id: rule.id, enabled })}
                />
              </li>
            ))}
          </ul>
        </QueryBoundary>
      </Card>

      <Card>
        <CardHeader title={t('recording.retention')} description={t('recording.retentionDesc')} />
        <CardBody className="grid gap-4 sm:grid-cols-3">
          {[
            ['recording.retention.audio', 'recording.retention.audioValue'],
            ['recording.retention.transcript', 'recording.retention.transcriptValue'],
            ['recording.retention.summary', 'recording.retention.summaryValue'],
          ].map(([labelKey, valueKey]) => (
            <div key={labelKey} className="rounded-md border border-border p-3">
              <p className="text-sm text-fg-muted">{t(labelKey)}</p>
              <p className="mt-0.5 text-md font-semibold text-fg">{t(valueKey)}</p>
            </div>
          ))}
        </CardBody>
      </Card>
    </div>
  )
}
